import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Clock, Edit3, Users2, Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * RecruitmentMarginPublic — public route ``/recruitment-margin/:token``.
 *
 * Approver (default caring@caring.pt) receives a magic link when the
 * recruitment margin falls below 33%. Three actions:
 *   • Aprovar — proceed as-is
 *   • Editar  — suggest new revenue + af_cost values (consultor applies manually)
 *   • Rejeitar — force renegotiation
 * Decision notifies the requesting consultor by email.
 */

const EUR = (v) => new Intl.NumberFormat("pt-PT", {
  style: "currency", currency: "EUR",
}).format(Number(v || 0));

const STATUS_META = {
  pending:   { label: "Pendente", icon: Clock, tone: "text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border-[hsl(var(--warning))]/25" },
  approved:  { label: "Aprovado", icon: CheckCircle2, tone: "text-[hsl(var(--success))] bg-[hsl(var(--success-bg))] border-[hsl(var(--success))]/25" },
  edited:    { label: "Novos valores sugeridos", icon: Edit3, tone: "text-[hsl(var(--info))] bg-[hsl(var(--info-bg))] border-[hsl(var(--info))]/25" },
  rejected:  { label: "Rejeitado", icon: XCircle, tone: "text-[hsl(var(--danger))] bg-[hsl(var(--danger-bg))] border-[hsl(var(--danger))]/25" },
  expired:   { label: "Expirado", icon: AlertTriangle, tone: "text-muted-foreground bg-muted/40 border-border" },
  cancelled: { label: "Cancelado", icon: XCircle, tone: "text-muted-foreground bg-muted/40 border-border" },
};

export default function RecruitmentMarginPublic() {
  const { token } = useParams();
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState(null);  // "approve" | "edit" | "reject" | null
  const [suggRev, setSuggRev] = useState("");
  const [suggCost, setSuggCost] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/recruitment-margin/${token}`)
      .then((r) => { if (!cancelled) setReq(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || "Pedido não encontrado."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const decide = async () => {
    if (!email.trim()) {
      toast.error("Introduz o teu email para validar a identidade.");
      return;
    }
    if (!mode) {
      toast.error("Escolhe uma decisão.");
      return;
    }
    const body = {
      decision: mode,
      approver_email: email.trim(),
      decision_reason: reason.trim() || null,
    };
    if (mode === "edit") {
      const sr = Number(suggRev), sc = Number(suggCost);
      if (!(sr > 0) || !(sc > 0)) {
        toast.error("Ao editar, indica os dois valores sugeridos (> 0).");
        return;
      }
      body.suggested_revenue = sr;
      body.suggested_af_cost = sc;
    }
    setBusy(true);
    try {
      const r = await api.post(`/recruitment-margin/${token}/decide`, body);
      setReq(r.data);
      const label = mode === "approve" ? "aprovado" : mode === "reject" ? "rejeitado" : "com sugestão registada";
      toast.success(`Pedido ${label}. O consultor foi notificado por email.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao registar decisão.");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
      </div>
    );
  }
  if (err || !req) {
    return (
      <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
        <div className="max-w-md bg-card border border-border rounded-xl p-8 text-center">
          <AlertTriangle className="w-10 h-10 text-muted-foreground mx-auto mb-4" strokeWidth={1.4} />
          <h1 className="font-heading text-lg text-foreground">Pedido não encontrado</h1>
          <p className="text-[13px] text-muted-foreground mt-2">{err || "O link pode ter expirado."}</p>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[req.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  const isPending = req.status === "pending";
  const marginPct = Number(req.current_margin_pct || 0);
  const threshold = Number(req.threshold_pct || 33);

  return (
    <div className="min-h-screen bg-muted/40 p-6 lg:p-10" data-testid="margin-auth-root">
      <div className="max-w-2xl mx-auto space-y-6">

        <header className="bg-card border border-border rounded-xl p-6">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-lg bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))] flex items-center justify-center shrink-0">
              <Users2 className="w-5 h-5" strokeWidth={1.6} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-heading font-semibold">
                Caring · Margem abaixo do mínimo
              </div>
              <h1 className="font-heading text-2xl tracking-tight text-foreground leading-tight">
                Autorização de recrutamento
              </h1>
              <div className="mt-2 text-[13px] text-muted-foreground">
                Cliente: <span className="text-foreground font-medium">{req.lead_name}</span> ·
                Pedido por <span className="text-foreground font-medium">{req.requested_by_name || "consultor"}</span>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium ${meta.tone}`}
                  data-testid={`margin-auth-status-${req.status}`}>
              <StatusIcon className="w-3 h-3" /> {meta.label}
            </span>
          </div>
        </header>

        <section className="bg-card border border-border rounded-xl p-6" data-testid="margin-auth-snapshot">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading font-semibold mb-4">
            Snapshot do recrutamento
          </div>
          <dl className="grid grid-cols-2 gap-6">
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Receita cliente / mês</dt>
              <dd className="text-[15px] font-heading text-foreground tabular-nums mt-1">{EUR(req.current_revenue)}</dd>
            </div>
            <div>
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Custo AFs / mês</dt>
              <dd className="text-[15px] font-heading text-foreground tabular-nums mt-1">{EUR(req.current_af_cost)}</dd>
            </div>
            <div className="col-span-2 border-t border-border/60 pt-4">
              <dt className="text-[11px] uppercase tracking-wider text-[hsl(var(--danger))] font-semibold">Margem actual</dt>
              <dd className="text-3xl font-heading font-bold text-[hsl(var(--danger))] tabular-nums mt-1">
                {marginPct.toFixed(1).replace(".", ",")}%
                <span className="text-[13px] text-muted-foreground font-medium ml-2">
                  (mínimo {threshold.toFixed(0)}% · faltam {(threshold - marginPct).toFixed(1)} p.p.)
                </span>
              </dd>
            </div>
          </dl>
        </section>

        {req.reason && (
          <section className="bg-card border border-border border-l-4 border-l-blue-500 rounded-xl p-6" data-testid="margin-auth-reason">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-heading font-semibold mb-2">
              Motivo do consultor
            </div>
            <p className="text-[14px] text-foreground leading-relaxed whitespace-pre-wrap">{req.reason}</p>
          </section>
        )}

        {isPending ? (
          <section className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="margin-auth-decision">
            <h2 className="font-heading text-lg tracking-tight text-foreground">Decisão</h2>
            <p className="text-[13px] text-muted-foreground">
              Aprovador esperado: <b>{req.approver_email}</b>. Um admin activo pode também decidir.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <button type="button"
                onClick={() => setMode("approve")}
                data-testid="margin-decision-approve"
                className={`h-11 rounded-lg text-sm font-semibold border flex items-center justify-center gap-1.5 transition ${
                  mode === "approve"
                    ? "bg-emerald-600 border-emerald-600 text-white"
                    : "bg-card border-emerald-600/40 text-emerald-700 hover:bg-emerald-50"
                }`}>
                <CheckCircle2 className="w-4 h-4" /> Aprovar
              </button>
              <button type="button"
                onClick={() => setMode("edit")}
                data-testid="margin-decision-edit"
                className={`h-11 rounded-lg text-sm font-semibold border flex items-center justify-center gap-1.5 transition ${
                  mode === "edit"
                    ? "bg-[#B08A5B] border-[#B08A5B] text-white"
                    : "bg-card border-[#B08A5B]/40 text-[#8B6C31] hover:bg-amber-50"
                }`}>
                <Edit3 className="w-4 h-4" /> Editar
              </button>
              <button type="button"
                onClick={() => setMode("reject")}
                data-testid="margin-decision-reject"
                className={`h-11 rounded-lg text-sm font-semibold border flex items-center justify-center gap-1.5 transition ${
                  mode === "reject"
                    ? "bg-rose-600 border-rose-600 text-white"
                    : "bg-card border-rose-600/40 text-rose-700 hover:bg-rose-50"
                }`}>
                <XCircle className="w-4 h-4" /> Rejeitar
              </button>
            </div>

            {mode === "edit" && (
              <div className="border border-[#B08A5B]/30 rounded-lg bg-amber-50/40 p-4 space-y-3" data-testid="margin-edit-fields">
                <div className="text-[11px] uppercase tracking-wider text-[#8B6C31] font-semibold">
                  Novos valores sugeridos
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-[11px] text-muted-foreground font-semibold mb-1">Receita cliente / mês</div>
                    <div className="relative">
                      <input type="number" step="0.01" min="0"
                        value={suggRev} onChange={(e) => setSuggRev(e.target.value)}
                        data-testid="margin-suggested-revenue"
                        className="w-full h-10 px-3 pr-7 text-right border border-border rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary/25" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                    </div>
                  </label>
                  <label className="block">
                    <div className="text-[11px] text-muted-foreground font-semibold mb-1">Custo AFs / mês</div>
                    <div className="relative">
                      <input type="number" step="0.01" min="0"
                        value={suggCost} onChange={(e) => setSuggCost(e.target.value)}
                        data-testid="margin-suggested-af-cost"
                        className="w-full h-10 px-3 pr-7 text-right border border-border rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary/25" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                    </div>
                  </label>
                </div>
                {suggRev && suggCost && Number(suggRev) > 0 && Number(suggCost) > 0 && (
                  <div className="text-[12px] text-muted-foreground">
                    Nova margem: <b>{(((suggRev - suggCost) / suggRev) * 100).toFixed(1)}%</b>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                O teu email
              </label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder={req.approver_email || "aprovador@caring.pt"}
                data-testid="margin-auth-email"
                className="w-full h-10 px-3 border border-border rounded-md text-[14px] focus:outline-none focus:ring-2 focus:ring-primary/25" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                Notas / contexto (opcional)
              </label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
                placeholder="Ex: aprovado como excepção estratégica; ou renegocia o preço com o cliente…"
                data-testid="margin-auth-notes"
                className="w-full px-3 py-2 border border-border rounded-md text-[13px] resize-none focus:outline-none focus:ring-2 focus:ring-primary/25" />
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={decide} disabled={busy || !mode}
                data-testid="margin-auth-submit"
                className="h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-60 flex items-center gap-2 font-heading">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-4 h-4" />}
                Registar decisão
              </button>
            </div>
          </section>
        ) : (
          <section className={`bg-card border rounded-xl p-6 ${meta.tone}`} data-testid="margin-auth-decided">
            <div className="text-[10px] uppercase tracking-[0.2em] font-heading font-semibold mb-2 opacity-70">Decisão registada</div>
            <p className="text-[13px]">
              {meta.label} por <b>{req.decided_by_name || req.decided_by_email}</b> em{" "}
              <span className="tabular-nums">{req.decided_at?.slice(0, 16).replace("T", " ")}</span>
            </p>
            {req.status === "edited" && (
              <p className="text-[13px] mt-2">
                Sugestão: receita <b>{EUR(req.suggested_revenue)}</b> · custo AFs <b>{EUR(req.suggested_af_cost)}</b>
              </p>
            )}
            {req.decision_reason && <p className="text-[13px] mt-2 whitespace-pre-wrap opacity-90">{req.decision_reason}</p>}
          </section>
        )}

        <footer className="text-center text-[11px] text-muted-foreground pt-4">
          Pedido criado em {req.requested_at?.slice(0, 16).replace("T", " ")} · expira em {req.expires_at?.slice(0, 16).replace("T", " ")}
        </footer>
      </div>
    </div>
  );
}
